/**
 * Live projected match score for the Coach Resume optimization flow.
 *
 * MIRRORS lms-backend/src/services/placement-services/coachResumeProjection.util.js.
 * The LMS recomputes the real score after the student confirms, so if the two
 * disagree the student sees one number in the builder and a different one on their
 * job card. coach-live-score.spec.ts pins the shared fixtures — if you change the
 * arithmetic here, change it there and in the LMS util too.
 *
 * Why project client-side at all: the authoritative score needs a multi-second
 * structured LLM call, which cannot run on every accept/reject. Only one of the six
 * components (`skillOverlap`) responds to resume edits, so the LMS ships the other
 * five collapsed into `fixedContribution` and we recompute the editable half locally.
 */

export type CoachScoreWeights = Record<string, number>;

export type CoachProjectionInputs = {
  fixedContribution: number;
  weights: CoachScoreWeights;
  roleDomainFit: number;
  skillOverlap: number;
  ceiling?: number;
};

// The only component a resume edit can move. Role & Domain Fit moved to a
// preference-vs-domain judgment on the LMS side and is no longer résumé-editable.
export const RESUME_EDITABLE_COMPONENTS = ["skillOverlap"] as const;

// Tier cutoffs shared with the student app's ScoreBreakdownDrawer/MatchScoreBadge.
// Keep these three in step or the same score gets two different labels.
export const STRONG_MATCH_THRESHOLD = 75;
export const GOOD_MATCH_THRESHOLD = 50;

const round2 = (value: number) => Math.round(value * 100) / 100;

const clampScore = (value: number) => Math.min(100, Math.max(0, Number(value) || 0));

/**
 * Project a total score from the fixed floor plus the two editable components.
 * Mirrors jobMatcher.assembleScore's weighted sum for the editable half.
 */
export const projectTotalScore = ({
  fixedContribution,
  weights,
  roleDomainFit,
  skillOverlap,
  ceiling,
}: CoachProjectionInputs) => {
  const total =
    (Number(fixedContribution) || 0) +
    (clampScore(roleDomainFit) * (Number(weights.roleDomainFit) || 0)) / 100 +
    (clampScore(skillOverlap) * (Number(weights.skillOverlap) || 0)) / 100;

  const capped = typeof ceiling === "number" ? Math.min(total, ceiling) : total;

  return round2(Math.min(100, Math.max(0, capped)));
};

/**
 * Tier label for a score, matching the student app's vocabulary exactly.
 */
export const getTierLabel = (score: number) => {
  if (score >= STRONG_MATCH_THRESHOLD) return "Strong Match";
  if (score >= GOOD_MATCH_THRESHOLD) return "Good Match";
  return "Stretch Role";
};

/**
 * Tailwind text colour for a score tier. Mirrors the student drawer's palette
 * (#5AF28E / #FFE836 / #DA6C5D) via the existing semantic classes.
 */
export const getTierColorClass = (score: number) => {
  if (score >= STRONG_MATCH_THRESHOLD) return "text-success";
  if (score >= GOOD_MATCH_THRESHOLD) return "text-warning";
  return "text-error";
};

const normalizeText = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(normalizeText).join(" ");
  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>)
      .map(normalizeText)
      .join(" ");
  }
  return "";
};

/**
 * Flatten a resume's data into one lowercase searchable string.
 *
 * HTML is stripped because the rich-text fields the AI rewrites are stored as
 * markup, and a keyword split across tags would otherwise be missed.
 */
export const flattenResumeText = (resumeData: unknown) =>
  normalizeText(resumeData)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();

/**
 * Which of the target keywords the draft resume now covers.
 *
 * Substring matching on word boundaries where possible: "React" should match
 * "ReactJS" but "R" should not match everything.
 *
 * Returns strings only. The LMS declares `missingSkills`/`presentSkills` as
 * `string[]`, but `Job.requiredSkills` is a JSON column of `{ skillName }` objects, so
 * a stale handoff payload (or one already persisted to sessionStorage) can carry
 * objects through. Emitting them unchanged crashed the builder's score panel with
 * "Objects are not valid as a React child", so the label is normalized here — the one
 * place every consumer reads keywords from.
 */
export const toKeywordLabel = (keyword: unknown): string => {
  if (typeof keyword === "string") return keyword.trim();
  if (typeof keyword === "number") return String(keyword);
  if (keyword && typeof keyword === "object") {
    const record = keyword as Record<string, unknown>;
    const candidate =
      record.skillName ?? record.skillsName ?? record.name ?? record.skill ?? record.value;

    return typeof candidate === "string" || typeof candidate === "number"
      ? String(candidate).trim()
      : "";
  }

  return "";
};

export const resolveKeywordCoverage = (resumeText: string, keywords: unknown[]) => {
  const covered: string[] = [];
  const missing: string[] = [];

  for (const keyword of keywords) {
    const label = toKeywordLabel(keyword);
    if (!label) continue;

    if (resumeText.includes(label.toLowerCase())) covered.push(label);
    else missing.push(label);
  }

  return { covered, missing };
};

/**
 * Keyword list reduced to non-empty, de-duplicated strings.
 *
 * Applied where the handoff payload crosses into persisted state, so sessionStorage
 * never holds a shape the score panel cannot render.
 */
export const normalizeKeywordList = (keywords: unknown): string[] => {
  if (!Array.isArray(keywords)) return [];

  return [...new Set(keywords.map((keyword) => toKeywordLabel(keyword)).filter(Boolean))];
};

/**
 * Projected `skillOverlap` sub-score from keyword coverage.
 *
 * Deliberately conservative: with no keywords to chase we return the baseline
 * rather than inventing an improvement, because claiming a gain we cannot justify
 * is worse than showing none.
 */
export const projectSkillOverlap = ({
  baselineScore,
  coveredCount,
  totalCount,
}: {
  baselineScore: number;
  coveredCount: number;
  totalCount: number;
}) => {
  if (totalCount <= 0) return clampScore(baselineScore);

  const coverage = (coveredCount / totalCount) * 100;

  // Never report worse than the stored baseline: the student is adding to the
  // resume, and a keyword list that happens to miss existing strengths should not
  // read as a regression.
  return clampScore(Math.max(baselineScore, coverage));
};


